import { MESSAGE } from '@vindr/services/constants/message.constant';
import { Form, Input } from 'antd';
import { FormInstance, Rule } from 'antd/es/form';
import { ChangeEvent, ComponentProps, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormItemProps } from '../templates/FormProps';

export type FormInputBaseProps<T> = Omit<FormItemProps<T>, 'form'> &
  Omit<ComponentProps<typeof Input>, 'form'> & {
    trigger?: string;
  };

interface FormInputNumberProps<T> extends FormInputBaseProps<T> {
  type: 'number';
  negativeNumber?: boolean;
  allowZero?: boolean;
  form: FormInstance<T>;
}

interface FormInputTextProps<T> extends FormInputBaseProps<T> {
  type?: 'text';
}

type FormInputProps<T> = FormInputNumberProps<T> | FormInputTextProps<T>;

const FormInput = <T,>({
  name,
  label,
  placeholder,
  rules,
  disabled,
  required,
  type = 'text',
  className,
  noStyle,
  trigger,
  ...props
}: FormInputProps<T>): JSX.Element => {
  const { t } = useTranslation('Vindoc');
  const [value, setValue] = useState<string | number>(0);

  const isNumberType = useMemo(() => type === 'number', [type]);
  const {
    negativeNumber = false,
    allowZero = false,
    form = undefined,
  } = isNumberType ? (props as FormInputNumberProps<T>) : {};

  const rulesMemo = useMemo(() => {
    const rulesState: Rule[] = [];

    if (required) {
      rulesState.push({
        required: true,
        message: t(MESSAGE.INLINE.IEM_0001),
      });
    }

    if (isNumberType) {
      rulesState.push({
        validator: (rule, value) => {
          if (value != null && isNaN(Number(value)))
            return Promise.reject(t('Please enter a number'));

          return Promise.resolve();
        },
        // message: t('Please enter a number'),
      });

      if (!negativeNumber) {
        rulesState.push({
          validator: (rule, value) => {
            if (Number(value) < 0) return Promise.reject(t('Please enter a positive number'));

            return Promise.resolve(true);
          },
          // message: t('Please enter a positive number'),
        });
      }
    }

    if (rules) rulesState.push(...rules);

    return rulesState;
  }, [t, required, rules, negativeNumber, isNumberType]);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (isNumberType) {
        const inputValue = e.target.value;
        setValue(inputValue);

        const num = Number(inputValue);

        if (!form) {
          console.error(form);
          throw new Error('Need pass form for validate input change');
        }

        if (inputValue === '' || (!allowZero && num === 0)) {
          form.setFieldValue(name, '');

          return;
        }

        if (!isNaN(num)) {
          form.setFieldValue(name, num);
        }
      }
    },
    [allowZero, form, isNumberType, name]
  );

  return (
    <Form.Item
      name={name}
      {...(label && { label: t(label) })}
      rules={rulesMemo}
      className={className}
      noStyle={noStyle}
      {...(isNumberType &&
        !negativeNumber &&
        Number(value) < 0 && {
          validateStatus: 'error',
          help: t('Please enter a positive number'),
        })}
      {...(trigger && { trigger })}
      required={required}
    >
      <Input
        placeholder={t(placeholder)}
        disabled={disabled}
        onChange={handleChange}
        {...(props as Omit<FormInputProps<T>, 'form'>)}
      />
    </Form.Item>
  );
};

export default FormInput;
